import { OPTIMISTIC_HOUR_IDENTIFIER } from 'redux/modules/hours.types';
import type { Hour } from 'types/hour';
import { timestamp } from 'utils';
import * as hourUtils from 'utils/hours';
import { insertItemInSequence, moveItemInSequence, removeItemFromSequence } from './resequencer';

// sets a value but also updates the updated_at
const updateHour = <T>(hour: Hour<T>, update: Partial<Hour<T>>): Hour<T> => {
  return { ...hour, ...update, updated_at: timestamp() };
};

interface HoursResequencerArgs<T> {
  dropEffect: 'copy' | 'move';
  source: Hour<T>;
  position: number | null | undefined;
  sourceDay: string;
  targetDay: string;
  hours: Array<Hour<T>>;
  newUserId?: number;
}

export const hoursResequencer = <T>(
  opts: HoursResequencerArgs<T>
): {
  hour: Hour<T>;
  list: Array<Hour<T>>;
} => {
  const sourceHour = opts.source;

  const targetPosition = opts.position;
  const targetDay = opts.targetDay;
  const sourceDay = opts.sourceDay;
  const hours = opts.hours;
  const workingHour = hourUtils.changeDay(sourceHour, targetDay);

  if (opts.dropEffect === 'copy') {
    const targetList = hours.filter(hour => hour.day && hour.day === targetDay);
    workingHour.id = OPTIMISTIC_HOUR_IDENTIFIER;

    const { sequenceItem: hour, list: targetListOutput } = insertItemInSequence(
      workingHour,
      targetPosition,
      targetList,
      updateHour
    );

    return {
      hour,
      list: targetListOutput,
    };
  } else if (sourceDay === targetDay && !opts.newUserId) {
    const sourceList = hours.filter(hour => hour.day && hour.day === sourceDay);
    const { sequenceItem: hour, list } = moveItemInSequence(
      workingHour,
      targetPosition,
      sourceList,
      updateHour
    );
    return { hour, list };
  } else {
    const sourceList = hours.filter(hour => hour.day && hour.day === sourceDay);
    const targetList = hours.filter(hour => hour.day && hour.day === targetDay);
    const sourceListOutput = removeItemFromSequence(sourceHour, sourceList, updateHour);
    const { sequenceItem: hour, list: targetListOutput } = insertItemInSequence(
      workingHour,
      targetPosition,
      targetList,
      updateHour
    );
    return { hour, list: [...sourceListOutput, ...targetListOutput] };
  }
};
